import * as angular from 'angular';
import * as _ from 'lodash';

import { IwidgetService, IBindingResult } from '../../services';
import { IWidget } from '../../widgets';
import { guidEmpty } from '../../common/constants';
import { ISeriesService } from './series.service';
import { IValueViewModel, Group, SeriesByWidget } from '../../models';
import { IIdentifierVmService } from '../../services/vm';
import { IGroupService } from '../../services/data';
import { BindingIssuesModalConfig } from '../../components/bindingIssuesModal/bindingIssuesModalConfig';

interface IntermediateObject {
    bindResult: IBindingResult;
    outside: { reference: Group, list: IValueViewModel[] };
    seriesByWidget: SeriesByWidget;
}

interface GroupWithBoundSeries {
    reference: Group;
    list: IValueViewModel[];
}

export interface IBindingCheckerService {
    checkBindings(newRef: string, oldRef: string, widgets: IWidget[]): angular.IPromise<IntermediateObject>;
    remapBoundSeries(seriesByWidget: SeriesByWidget, bindingResult: IBindingResult): void;
    createModal(object: IntermediateObject, widgets: IWidget[]): BindingIssuesModalConfig;
}

export class BindingCheckerService implements IBindingCheckerService {
    static serviceId = 'bindingCheckerService';

    static $inject = [
        '$q',
        'widgetService',
        'seriesService',
        'identifierVmService',
        'groupService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _widgetService: IwidgetService,
        private _seriesService: ISeriesService,
        private _identifierVmService: IIdentifierVmService,
        private _groupService: IGroupService
    ) {
    }
    /**
     * Checks bindings on widget series.
     * @param newRef The new reference group guid.
     * @param oldRef The old reference group guid.
     * @param widgets The list of widgets to update.
     */
    checkBindings(newRef: string, oldRef: string, widgets: IWidget[]): angular.IPromise<IntermediateObject> {
        const seriesByWidget: SeriesByWidget = {};
        let seriesGuids: string[] = [];
        if (widgets && widgets.length) {
            widgets.forEach(w => {
                const series = this._widgetService.getSeries(w);
                if (series) {
                    seriesByWidget[w.guid] = series;
                    seriesGuids = seriesGuids.concat(series.filter(s => s.isBoundToGroup).map(s => s.guid));
                }
            });
            seriesGuids = _.uniq(seriesGuids);
        }
        // init edge case
        const original = oldRef === guidEmpty
            ? newRef
            : oldRef;

        let bindResult: IBindingResult;

        // aggregate all series
        return this._seriesService.bindToReference(seriesGuids, original, newRef)
            .then(bound => {
                bindResult = bound;
                const values = Object.keys(bound.map).map(k => bound.map[k] ? bound.map[k] : k);
                return this.seriesOutsideRefenceGroup(values, newRef);
            })
            .then(data => {
                return {
                    bindResult,
                    outside: data,
                    seriesByWidget
                };
            });
    }


    /**
     * Remap bound series
     * @param seriesByWidget
     * @param bindingResult
     */
    remapBoundSeries(seriesByWidget: SeriesByWidget, bindingResult: IBindingResult): void {
        const map = bindingResult.map;
        Object.keys(seriesByWidget).forEach(widgetGuid => {
            seriesByWidget[widgetGuid].filter(s => s.isBoundToGroup).forEach(serie => {
                delete serie['relativePath'];
                if (map[serie.guid]) {
                    serie.guid = map[serie.guid];
                }
            });
        });
    }

    createModal(object: IntermediateObject, widgets: IWidget[]): BindingIssuesModalConfig {
        const { outside, bindResult, seriesByWidget } = object;

        const errorsViewModels = outside.list.map(vm => {
            const widgetNames = widgets
                .filter(w => _.some(seriesByWidget[w.guid], s => s.guid === vm.guid))
                .map(w => w.name);

            return {
                name: vm.name,
                path: vm.groupAbsolutePath,
                widgets: widgetNames.join(', ')
            };
        });

        const modalConfig = {
            bindResult: bindResult,
            seriesByWidget: seriesByWidget,
            outside: outside.list,
            errors: errorsViewModels,
            reference: outside.reference,
            totalCount: _.sum(Object.keys(seriesByWidget).map(k => seriesByWidget[k].length))
        };
        return modalConfig;
    }

    /**
      * Handle series outside reference group
      * @param guids
      */
    private seriesOutsideRefenceGroup(guids: string[], referenceGroup: string): angular.IPromise<GroupWithBoundSeries> {
        const defered = this._$q.defer<GroupWithBoundSeries>();
        let group: Group = null;

        if (!guids || guids.length === 0) {
            defered.resolve({ reference: null, list: [] });
        } else {
            this._groupService.getByGuid(referenceGroup)
                .then(data => {
                    group = data.group;
                    return this._identifierVmService.getAllViewModels();
                })
                .then(vmList => {
                    const result = this.buildTuple(guids, group, vmList);
                    defered.resolve(result);
                })
                .catch(reason => defered.reject(reason));
        }

        return defered.promise;
    }

    private buildTuple(seriesGuids: string[], group: Group, vmList: IValueViewModel[]): GroupWithBoundSeries {
        const vmIndex: { [guid: string]: IValueViewModel } = {};
        vmList.forEach(vm => vmIndex[vm.guid] = vm);
        const seriesVm = seriesGuids.map(g => vmIndex[g]).filter(vm => vm);
        const list = seriesVm.filter(vm => !_.startsWith(vm.groupAbsolutePath, group.absoluteName));
        const result = {
            reference: group,
            list: list
        };
        return result;
    }

}